/*
  DrinksHero — Klaviyo form UV2ZCy mobile overlay safeguard
  Added: 2026-09-07 (R1.2 CART AUDIT). Mobile only. Scoped to form UV2ZCy only.

  Problem, measured live on mobile (375x812, product page):
    Klaviyo's modal shell is a full-viewport fixed element at z-index 90000.
    Closing it starts a 0.35s "klaviyo-fadeout" CSS animation; the element is
    only removed from the DOM when that animation finishes. Throughout the
    fade it keeps pointer-events:auto and continues to cover the whole
    viewport. Sampled after a close-button tap, document.elementFromPoint() at
    the Add to Cart centre returned the popup's email input at t=0, 30, 50 and
    100ms, and only returned the Add to Cart button from ~400ms. A tap in that
    window is swallowed. If the animation never completes (backgrounded tab,
    reduced motion, interrupted paint) the overlay never leaves at all.

  Live DOM identifiers observed during the audit (measured, not guessed):
    form     -> form[data-testid="klaviyo-form-UV2ZCy"]
    overlay  -> div[role="dialog"][data-kl-scroll-locking-modal="true"]
                (position:fixed; z-index:90000; ancestor of the form)
    close    -> button.klaviyo-close-form
    open     -> overlay inline style animation-name: "klaviyo-fadein"
                (set for the whole time the popup is open, fill-mode forwards)
    closing  -> overlay inline style animation-name: "klaviyo-fadeout"
                (0.35s, fill-mode forwards — the close signal)
    closed record -> localStorage "klaviyoOnsite"
                     .viewedForms.modal.disabledForms.UV2ZCy.lastCloseTime

    Note: getComputedStyle(overlay).opacity reads "0" while the popup is fully
    visible, because the visible value comes from the running keyframes rather
    than the base style. Raw opacity is therefore NOT a safe "is it hidden"
    test here — animation-name is. An earlier revision of this file used
    opacity and killed the popup during its fade-IN.

  Scheduling:
    setTimeout, NOT requestAnimationFrame. rAF is suspended whenever the page
    is not being painted (hidden tab, backgrounded browser, throttled view).
    The overlay must still be neutralised in that state, or it is left
    intercepting taps the moment the user returns. Verified: with rAF the
    guard never fired at all while the view was hidden.

  Lifecycle contract:
    - Nothing is started at all above 749px. A matchMedia listener starts the
      guard only if the viewport later becomes narrow (rotation / resize).
    - At most ONE pending timer exists at any moment (SCHEDULED flag), plus at
      most one delayed re-check after a close tap. Both are tracked and cleared
      by teardown().
    - Discovery (the body observer) is bounded: if the UV2ZCy form has not
      appeared within DISCOVERY_MS it disconnects, so pages that never show
      this popup are not observed for the life of the page.
    - Once the overlay is neutralised, or has left the DOM, teardown() runs:
      every observer, listener and timer is released. Klaviyo records the form
      as closed at that point and does not re-show it in the same page view.
*/
(function () {
  'use strict';

  var FORM_ID = 'UV2ZCy';
  var FORM_SELECTOR = 'form[data-testid="klaviyo-form-' + FORM_ID + '"]';
  var OVERLAY_SELECTOR = 'div[role="dialog"][data-kl-scroll-locking-modal="true"]';
  var CLOSE_SELECTOR = 'button.klaviyo-close-form';
  var ANIM_OPEN = 'klaviyo-fadein';
  var ANIM_CLOSE = 'klaviyo-fadeout';
  var FLAG = 'data-dh-uv2zcy-neutralised';
  var MOBILE_QUERY = '(max-width: 749px)';
  var STORAGE_KEY = 'klaviyoOnsite';
  var DISCOVERY_MS = 60000; // Klaviyo shows this popup ~5-7s in; 60s is ample.

  if (!document.querySelector || !Element.prototype.closest) return;

  var started = false;
  var stopped = false;
  var overlay = null;
  var overlayObserver = null;
  var discoveryObserver = null;
  var scheduled = false;
  var tickTimer = null;
  var delayedTimer = null;
  var discoveryTimer = null;
  var styleEl = null;

  function isMobile() {
    try {
      return !!(window.matchMedia && window.matchMedia(MOBILE_QUERY).matches);
    } catch (e) {
      return false;
    }
  }

  // ---- teardown -----------------------------------------------------------
  // Releases every observer, listener and timer this guard owns.
  function teardown() {
    stopped = true;
    scheduled = false;
    if (tickTimer) { clearTimeout(tickTimer); tickTimer = null; }
    if (delayedTimer) { clearTimeout(delayedTimer); delayedTimer = null; }
    if (discoveryTimer) { clearTimeout(discoveryTimer); discoveryTimer = null; }
    if (overlayObserver) { overlayObserver.disconnect(); overlayObserver = null; }
    if (discoveryObserver) { discoveryObserver.disconnect(); discoveryObserver = null; }
    if (overlay) {
      try {
        overlay.removeEventListener('animationend', schedule, true);
        overlay.removeEventListener('transitionend', schedule, true);
      } catch (e) {}
    }
    try { document.removeEventListener('click', onCloseClick, true); } catch (e) {}
    try { window.removeEventListener('klaviyoForms', onKlaviyoForms); } catch (e) {}
  }

  // ---- scheduling ---------------------------------------------------------
  // Exactly one pending tick at a time.
  function schedule() {
    if (stopped || scheduled) return;
    scheduled = true;
    tickTimer = window.setTimeout(function () {
      scheduled = false;
      tickTimer = null;
      try {
        evaluate();
      } catch (e) {}
    }, 0);
  }

  // ---- tracking -----------------------------------------------------------
  function trackOverlay() {
    var form = document.querySelector(FORM_SELECTOR);
    if (form) {
      var host = form.closest(OVERLAY_SELECTOR);
      if (host && host !== overlay) {
        overlay = host;
        watchOverlay(host);
        // The popup exists; discovery has done its job.
        if (discoveryObserver) { discoveryObserver.disconnect(); discoveryObserver = null; }
        if (discoveryTimer) { clearTimeout(discoveryTimer); discoveryTimer = null; }
      }
    }
    return overlay;
  }

  function watchOverlay(el) {
    try {
      if (overlayObserver) overlayObserver.disconnect();
      overlayObserver = new MutationObserver(schedule);
      overlayObserver.observe(el, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: true,
        subtree: true
      });
      el.addEventListener('animationend', schedule, true);
      el.addEventListener('transitionend', schedule, true);
    } catch (e) {}
  }

  // ---- state readers ------------------------------------------------------
  function animName(el) {
    return (el.style && el.style.animationName) || '';
  }

  function displayHidden(el) {
    var cs;
    try {
      cs = window.getComputedStyle(el);
    } catch (e) {
      return false;
    }
    return !!cs && (cs.display === 'none' || cs.visibility === 'hidden');
  }

  function recordedClosed() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return false;
      var modal = JSON.parse(raw);
      modal = modal && modal.viewedForms && modal.viewedForms.modal;
      if (!modal) return false;
      return !!(
        (modal.disabledForms && modal.disabledForms[FORM_ID]) ||
        (modal.closedForms && modal.closedForms[FORM_ID])
      );
    } catch (e) {
      return false;
    }
  }

  // ---- action -------------------------------------------------------------
  function neutralise(el) {
    if (el && el.isConnected && !el.hasAttribute(FLAG)) {
      el.setAttribute(FLAG, '');
      try {
        // pointer-events only: Klaviyo's own fade-out still plays out exactly
        // as designed, it simply can no longer intercept taps while it does.
        el.style.setProperty('pointer-events', 'none', 'important');
      } catch (e) {}
    }
    // Nothing further to monitor for this page view.
    teardown();
  }

  function evaluate() {
    if (stopped) return;
    if (!isMobile()) return;

    var el = trackOverlay();
    if (!el) return;

    // Overlay was tracked and has since left the DOM — Klaviyo cleaned up on
    // its own, so there is nothing left to guard.
    if (!el.isConnected) { teardown(); return; }
    if (el.hasAttribute(FLAG)) { teardown(); return; }

    // The popup is animating in / sitting open — leave it completely alone.
    if (animName(el) === ANIM_OPEN) return;

    if (
      animName(el) === ANIM_CLOSE ||          // close signal
      !el.querySelector(FORM_SELECTOR) ||     // form unmounted, shell left behind
      displayHidden(el) ||                    // hidden but still in the DOM
      recordedClosed()                        // Klaviyo has recorded it closed
    ) {
      neutralise(el);
    }
  }

  // ---- triggers -----------------------------------------------------------
  function onCloseClick(e) {
    if (stopped) return;
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest(CLOSE_SELECTOR)) return;
    if (!overlay || !overlay.contains(t)) return;
    // Never neutralise inside the click itself — let Klaviyo's own handler run.
    schedule();
    if (!delayedTimer) {
      delayedTimer = window.setTimeout(function () {
        delayedTimer = null;
        schedule();
      }, 120);
    }
  }

  function onKlaviyoForms(e) {
    if (stopped) return;
    var d = e && e.detail;
    if (!d || d.formId !== FORM_ID) return;
    if (!isMobile()) return;
    if (d.type === 'close') {
      var el = trackOverlay();
      if (el) neutralise(el);
    } else {
      // 'open' and friends: the popup is in play, take a look.
      schedule();
    }
  }

  // ---- start / stop -------------------------------------------------------
  function injectStyle() {
    if (styleEl) return;
    try {
      styleEl = document.createElement('style');
      styleEl.setAttribute('data-dh-uv2zcy-guard', '');
      styleEl.textContent =
        '@media screen and (max-width: 749px){' +
        '[' + FLAG + '],[' + FLAG + '] *{pointer-events:none !important;}' +
        '}';
      (document.head || document.documentElement).appendChild(styleEl);
    } catch (e) {}
  }

  function start() {
    if (started || stopped) return;
    if (!isMobile()) return;
    started = true;

    injectStyle();

    document.addEventListener('click', onCloseClick, true);
    window.addEventListener('klaviyoForms', onKlaviyoForms);

    try {
      discoveryObserver = new MutationObserver(schedule);
      discoveryObserver.observe(document.body, { childList: true, subtree: true });
    } catch (e) {}

    // Bounded discovery: on a page where UV2ZCy never appears, stop observing.
    // The klaviyoForms listener stays as a cheap re-arm if it shows up later.
    discoveryTimer = window.setTimeout(function () {
      discoveryTimer = null;
      if (!overlay && discoveryObserver) {
        discoveryObserver.disconnect();
        discoveryObserver = null;
      }
    }, DISCOVERY_MS);

    schedule();
  }

  function boot() {
    if (isMobile()) {
      start();
      return;
    }
    // Desktop: start nothing. Only arm if the viewport later becomes narrow.
    try {
      var mq = window.matchMedia(MOBILE_QUERY);
      var onChange = function () {
        if (mq.matches) start();
      };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    } catch (e) {}
  }

  if (document.body) {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot);
  }
})();
